/**
 * Coach Profile — Storage Adapter
 *
 * Persists the CoachProfile + a small history of prior snapshots so drift
 * comparisons survive across app restarts. Piggybacks on the app's existing
 * settings persistence (window.electronAPI.loadSettings/saveSettings) with
 * a localStorage fallback for browser/dev mode — no new file API needed.
 *
 * The shape inside settings:
 *   settings.coachProfile = {
 *     current: CoachProfile | null,
 *     history: SnapshotHistoryEntry[]   // newest-first, capped at HISTORY_MAX
 *   }
 *
 * SnapshotHistoryEntry stores ONLY the compact identity payload — full
 * profile entries with detail/action/evidence are not duplicated into the
 * history. That keeps the on-disk footprint tiny (~14KB for 12 snapshots
 * of 20 signatures each) so we can afford a generous history depth without
 * bloating settings.json.
 */

const STORAGE_KEY = 'coachProfile';
const LOCAL_STORAGE_KEY = 'tradingJournal_coachProfile';
// 12 snapshots is enough for ~3 months of weekly profile rebuilds — past
// that, week-over-week comparisons start losing signal anyway.
const HISTORY_MAX = 12;

const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

/**
 * Build the compact history entry from a full profile. We keep just the
 * fields drift code reads: signature lists per cluster + the timestamp.
 */
const compactSnapshot = (profile) => {
  if (!profile) return null;
  return {
    generatedAt: profile.generatedAt,
    edgeSignatures: (profile.edges || []).map(e => e.signature),
    leakSignatures: (profile.leaks || []).map(l => l.signature),
    edgeCount: (profile.edges || []).length,
    leakCount: (profile.leaks || []).length,
    resolvedCount: (profile.resolvedLeaks || []).length,
    tradeCount: profile.tradeCount || 0,
  };
};

/**
 * Read the stored profile + history. Returns
 *   { current: null, history: [] } when nothing is stored.
 *
 * Always async: uses Electron IPC when available, localStorage otherwise.
 */
export const readStoredProfile = async () => {
  try {
    if (isElectron && window.electronAPI.loadSettings) {
      const result = await window.electronAPI.loadSettings();
      if (result?.success) {
        const stored = result.settings?.[STORAGE_KEY];
        if (stored && typeof stored === 'object') {
          return {
            current: stored.current || null,
            history: Array.isArray(stored.history) ? stored.history : [],
          };
        }
      }
      return { current: null, history: [] };
    }
    // Browser / dev fallback
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return { current: null, history: [] };
    const parsed = JSON.parse(raw);
    return {
      current: parsed?.current || null,
      history: Array.isArray(parsed?.history) ? parsed.history : [],
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[CoachProfile] readStoredProfile failed:', e?.message || e);
    return { current: null, history: [] };
  }
};

/**
 * Write the new current profile + push the prior current onto history (only
 * if it had different signatures — re-running the same analysis twice
 * shouldn't pollute the history). Returns the updated { current, history }
 * so the caller can keep state in sync without re-reading.
 *
 * @param {CoachProfile} newProfile      The new current profile
 * @param {Object}       currentStored   What was previously stored
 *                                       (pass result of readStoredProfile)
 */
export const writeStoredProfile = async (newProfile, currentStored = { current: null, history: [] }) => {
  if (!newProfile) return currentStored;

  const priorSnapshot = compactSnapshot(currentStored.current);
  const newSnapshot = compactSnapshot(newProfile);

  // Only push the prior snapshot to history if its signature set differs
  // from the new one. Repeated rebuilds with no analyzer changes should
  // not multiply history entries.
  let history = currentStored.history || [];
  if (priorSnapshot) {
    const sameSignatures =
      newSnapshot &&
      priorSnapshot.edgeSignatures.length === newSnapshot.edgeSignatures.length &&
      priorSnapshot.leakSignatures.length === newSnapshot.leakSignatures.length &&
      priorSnapshot.edgeSignatures.every((s, i) => s === newSnapshot.edgeSignatures[i]) &&
      priorSnapshot.leakSignatures.every((s, i) => s === newSnapshot.leakSignatures[i]);
    if (!sameSignatures) {
      history = [priorSnapshot, ...history].slice(0, HISTORY_MAX);
    }
  }

  const updated = { current: newProfile, history };

  try {
    if (isElectron && window.electronAPI.saveSettings) {
      // ── Minimal payload (Phase 19.3 follow-up) ───────────────────
      // Pre-merge-fix, this code did a read-modify-write of the FULL
      // settings object to avoid clobbering other sections. That worked
      // because the save was a full overwrite — so we had to include
      // every key we wanted to preserve.
      //
      // Now that the save-settings IPC does a top-level merge (see
      // main.cjs), we should send ONLY the coachProfile key. Sending
      // the full settings object would be actively harmful: the read
      // happens at T=0, the save at T=10ms, and if TradingJournal's
      // debounced save fires at T=5ms with new values for other keys,
      // those new values would land on disk first — then OUR save
      // would arrive with the STALE pre-T=5ms values for those keys,
      // and the merge would overwrite the recent updates with our
      // stale snapshot.
      //
      // The fix is simple: only send what we own. The merge IPC
      // preserves everything else automatically.
      await window.electronAPI.saveSettings({ [STORAGE_KEY]: updated });
      return updated;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[CoachProfile] writeStoredProfile failed:', e?.message || e);
  }
  return updated;
};

/**
 * Clear stored profile — used by a "reset Coach memory" UI affordance.
 * Returns { current: null, history: [] } so callers can keep state in sync.
 *
 * Important: the save-settings IPC does a top-level merge (see
 * main.cjs:save-settings comment for rationale), which means a plain
 * `delete next[STORAGE_KEY]` followed by save would have NO effect —
 * the merge re-adds the existing key from disk. Instead we write the
 * key explicitly as `null`. The readStoredProfile guard
 * `if (stored && typeof stored === 'object')` correctly treats `null`
 * as "no profile" (null is falsy, fails the &&), so the read side sees
 * the same shape as if the key were absent.
 */
export const clearStoredProfile = async () => {
  try {
    if (isElectron && window.electronAPI.saveSettings) {
      // Write null explicitly — merge in main.cjs preserves any key not
      // mentioned, so we can't delete; null sentinel works because the
      // read-side guard treats it as absent.
      await window.electronAPI.saveSettings({ [STORAGE_KEY]: null });
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[CoachProfile] clearStoredProfile failed:', e?.message || e);
  }
  return { current: null, history: [] };
};

// Exports for tests
export { compactSnapshot, HISTORY_MAX, STORAGE_KEY, LOCAL_STORAGE_KEY };
